import type { GroupCycleRow, GroupWonCycle, KametiGroup, PayoutStatus } from "../services/types";
import { formatMonthYear } from "./format";

/**
 * Pure, client-side group arithmetic. The economics preview and the generated
 * cycle table are computed here for DISPLAY ONLY — real cycle figures come from
 * the backend once a group is active. Nothing here is a source of truth for money.
 */

/** Chit value = installment * member count. Arithmetic, never independently editable. */
export function chitValue(installment: number, memberCount: number): number {
  return installment * memberCount;
}

/** The number of monthly cycles always equals the number of members. */
export function durationMonths(memberCount: number): number {
  return memberCount;
}

/** Month + year label for a given cycle, anchored to the group start date. */
export function cyclePeriodLabel(startDate: string, cycle: number): string {
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return "--";
  const total = d.getMonth() + (cycle - 1);
  const year = d.getFullYear() + Math.floor(total / 12);
  return formatMonthYear(new Date(year, total % 12, 1));
}

/** Number of cycles completed so far = decided cycles. */
export function completedCycles(group: Pick<KametiGroup, "wonCycles">): number {
  return group.wonCycles.length;
}

/** Sum of every discount generated so far (pool − winning bid), reserved cycles contribute 0. */
export function totalDiscount(group: Pick<KametiGroup, "chitValue" | "wonCycles">): number {
  return group.wonCycles.reduce((sum, w) => sum + Math.max(0, group.chitValue - w.winningBid), 0);
}

/** The date of the next upcoming auction, or null if every cycle is decided. */
export function nextAuctionDate(group: Pick<KametiGroup, "startDate" | "auctionDay" | "memberCount" | "wonCycles">): string | null {
  const completed = group.wonCycles.length;
  const nextCycle = completed + 1;
  if (nextCycle > group.memberCount) return null;
  const start = new Date(group.startDate);
  if (Number.isNaN(start.getTime())) return null;
  const total = start.getMonth() + completed;
  const year = start.getFullYear() + Math.floor(total / 12);
  const day = Math.min(group.auctionDay, 28);
  return new Date(year, total % 12, day).toISOString();
}

/**
 * Build the full cycle table for a group. Decided cycles come from `wonCycles`;
 * everything else is pending. The reserved organizer cycle always pays the full
 * pool with no discount and is marked distinctly.
 */
export function buildCycles(group: Pick<KametiGroup, "startDate" | "monthlyInstallment" | "chitValue" | "memberCount" | "reservedCycle" | "status" | "wonCycles">): GroupCycleRow[] {
  const rows: GroupCycleRow[] = [];
  for (let c = 1; c <= group.memberCount; c++) {
    const isReserved = c === group.reservedCycle;
    const won = group.wonCycles.find((w) => w.cycle === c);
    let status: GroupCycleRow["status"] = "pending";
    if (group.status === "completed") status = "completed";
    else if (group.status === "cancelled") status = won ? "completed" : "cancelled";
    else if (won) status = "completed";

    let winningBid = 0;
    let discount = 0;
    let perMemberDividend = 0;
    let commissionPerMember = 0;
    let netInstallment = group.monthlyInstallment;
    let collectionStatus: "pending" | "partial" | "paid" | "overdue" = "pending";
    let collectedPct = 0;
    let payoutStatus: PayoutStatus | "pending" = "pending";

    if (won) {
      if (isReserved) {
        winningBid = group.chitValue;
        discount = 0;
      } else {
        winningBid = won.winningBid;
        discount = Math.max(0, group.chitValue - winningBid);
        perMemberDividend = discount > 0 ? Math.round(discount / group.memberCount) : 0;
      }
      netInstallment = group.monthlyInstallment - perMemberDividend;
      collectionStatus = "paid";
      collectedPct = 100;
      payoutStatus = "disbursed";
    }

    rows.push({
      cycle: c,
      periodLabel: cyclePeriodLabel(group.startDate, c),
      isOrganizerReserved: isReserved,
      winnerMemberId: won?.memberId ?? null,
      winningBid,
      discount,
      perMemberDividend,
      commissionPerMember,
      netInstallment,
      status,
      collectionStatus,
      collectedPct,
      payoutStatus,
    });
  }
  return rows;
}

/** Number of empty (unfilled) tickets in a group. */
export function emptyTicketCount(group: Pick<KametiGroup, "memberCount" | "tickets">): number {
  return Math.max(0, group.memberCount - group.tickets.length);
}

/** True once every ticket is filled and the organizer holds one of them. */
export function canActivate(group: Pick<KametiGroup, "memberCount" | "tickets" | "organizerMemberId">): boolean {
  const allFilled = group.tickets.length >= group.memberCount;
  const organizerAssigned = group.tickets.some((t) => t.memberId === group.organizerMemberId);
  return allFilled && organizerAssigned;
}

/* ---------- Live economics preview (illustrative only) ---------- */

export interface CycleExampleRow {
  key: "normal" | "reserved" | "final";
  label: string;
  note: string;
  pool: number;
  winningBid: number;
  discount: number;
  perMemberDividend: number;
  netInstallment: number;
  totalCollected: number;
}

const ILLUSTRATIVE_BID_RATE = 0.02;

/**
 * A worked example of three representative cycles so the organizer can see the
 * mechanics concretely. Illustrative only — never used as real money figures.
 */
export function computeCycleExamples(
  input: { monthlyInstallment: number; memberCount: number; reservedCycle: number }
): CycleExampleRow[] {
  const pool = chitValue(input.monthlyInstallment, input.memberCount);
  const inst = input.monthlyInstallment;
  const count = input.memberCount;

  // Normal auction: an example discount, shared as a dividend among members.
  const normalDiscount = Math.round(pool * ILLUSTRATIVE_BID_RATE);
  const normalDividend = Math.round(normalDiscount / count);
  const normal: CycleExampleRow = {
    key: "normal",
    label: "Normal auction",
    note: "Lowest bidder wins; the discount is shared back as a dividend.",
    pool,
    winningBid: pool - normalDiscount,
    discount: normalDiscount,
    perMemberDividend: normalDividend,
    netInstallment: inst - normalDividend,
    totalCollected: (inst - normalDividend) * count,
  };

  // Reserved organizer cycle: full pool, zero discount, everyone pays full.
  const reserved: CycleExampleRow = {
    key: "reserved",
    label: `Reserved cycle ${input.reservedCycle}`,
    note: "No auction — the organizer takes the full pool, so no dividend is generated.",
    pool,
    winningBid: pool,
    discount: 0,
    perMemberDividend: 0,
    netInstallment: inst,
    totalCollected: inst * count,
  };

  // Final cycle: only one member remains, no competition.
  const finalCycle: CycleExampleRow = {
    key: "final",
    label: `Final cycle ${input.memberCount}`,
    note: "Only one member remains, so they take the full pool with no auction.",
    pool,
    winningBid: pool,
    discount: 0,
    perMemberDividend: 0,
    netInstallment: inst,
    totalCollected: inst * count,
  };

  return [normal, reserved, finalCycle];
}

/** Build the next auto-generated group code: year-prefixed sequential. */
export function nextGroupCode(sequence: number): string {
  const year = new Date().getFullYear();
  return `${year}${String(sequence).padStart(4, "0")}`;
}